/**
 * Dynamic Content Utilities
 *
 * This module contains utility functions for building and merging dynamic content slots,
 * overlaying DCA results, and normalizing CF items.
 */
import {
  SlotMeta,
  DcaNode,
  CfNode,
  SlotContent,
  ExtendedCfNode,
} from '../organisms/DynamicTabbedLayout/DynamicTabbedLayout.types';

type NormalizableCfNode = (CfNode | ExtendedCfNode) & {
  iconAltText_1?: string;
  iconAltText_2?: string;
  iconAltText_3?: string;
  iconAltText_4?: string;
  iconPath_1?: string;
  iconPath_2?: string;
  iconPath_3?: string;
  iconPath_4?: string;
  ctaLinkLoggedIn?: string;
  ctaLinkTextLoggedIn?: string;
  ctaOpenInNewTabLoggedIn?: boolean;
  descriptionLoggedIn?: string;
  descriptionLoggedOut?: string;
  headerLoggedIn?: string;
  headerLoggedOut?: string;
  imageAltText?: string;
  primaryCtaLink?: string;
  primaryCtaLinkText?: string;
  primaryCtaOpenInNewTab?: boolean;
  secondaryCtaLink?: string;
  secondaryCtaLinkText?: string;
  secondaryCtaOpenInNewTab?: boolean;
  theme: string;
};

// Pattern builder — defines grid structure (banner/sixpack combos)
export const buildPatternSlots = (bannerFirst: boolean, squareFirst: boolean, dcaNodes: DcaNode[] = []): SlotMeta[] => {
  const row1 = squareFirst ? ['square', 'tall', 'square'] : ['tall', 'square', 'tall'];
  const row2 = squareFirst ? ['tall', 'square', 'tall'] : ['square', 'tall', 'square'];
  const sixpack = [...row1, ...row2];

  // Prepare authored DCA orders for fast lookup
  const dcaOrders = new Set<number>();
  for (let i = 0; i < (dcaNodes?.length || 0); i++) {
    const num = Number(dcaNodes[i]?.order);
    if (!Number.isNaN(num)) dcaOrders.add(num);
  }

  const typeForPosition = (position: number): 'dca' | 'cf' => (dcaOrders.has(position) ? 'dca' : 'cf');

  const mapSixpack = (start: number): SlotMeta[] =>
    sixpack.map((size, i) => {
      const position = start + i;
      return {
        size: size as 'square' | 'tall',
        position,
        type: typeForPosition(position),
      };
    });

  if (bannerFirst) {
    const firstSixStart = 2;
    const secondSixStart = 9;
    return [
      { size: 'banner', position: 1, type: typeForPosition(1) },
      ...mapSixpack(firstSixStart),
      { size: 'banner', position: 8, type: typeForPosition(8) },
      ...mapSixpack(secondSixStart),
    ];
  }

  const secondSixStart = 8;
  return [...mapSixpack(1), { size: 'banner', position: 7, type: typeForPosition(7) }, ...mapSixpack(secondSixStart)];
};

export function mergeCfAndDcaToSlots(
  slotsMeta: SlotMeta[],
  dcaNodes: DcaNode[],
  cfData: CfNode[]
): SlotContent[] | null {
  const len = slotsMeta?.length;
  const result = new Array(len);

  // Build a fast lookup map: order → DCA node
  const dcaByOrder = new Map<number, DcaNode>();
  for (let i = 0; i < dcaNodes.length; i++) {
    const order = dcaNodes[i].order;
    if (order !== undefined && order !== null) {
      const num = Number(order);
      if (!Number.isNaN(num)) dcaByOrder.set(num, dcaNodes[i]);
    }
  }

  for (let i = 0; i < len; i++) {
    const slot = slotsMeta[i];
    const position = typeof slot?.position === 'number' ? slot.position : i;
    const authoredDca = dcaByOrder.get(position);

    if (authoredDca) {
      result[i] = {
        ...slot,
        position,
        type: 'dca',
        payload: null,
        cfFallback: cfData[i] ?? null,
        dcaTarget: {
          order: authoredDca.order,
          mboxParameter: authoredDca.mboxParameter,
        },
      };
    } else {
      result[i] = {
        ...slot,
        position,
        type: 'cf',
        payload: cfData[i] ?? null,
        cfFallback: null,
      };
    }
  }
  return result;
}

// Overlay map onto slots (returns new slots array)
export function overlaySlotsWithDcaRes(
  slots: SlotContent[] = [],
  dcaNodes: DcaNode[] = [],
  opts?: { fillFromCFFallbackIfEmpty?: boolean }
) {
  const hasDca = Array.isArray(dcaNodes) && dcaNodes.length > 0;

  if (!hasDca && opts?.fillFromCFFallbackIfEmpty) {
    const filled = (slots || []).map(s => {
      if (s?.type === 'dca' && s?.cfFallback) {
        return { ...s, payload: s.cfFallback, type: 'dca' };
      }
      return s;
    });
    return filled;
  }

  const overlaid = (slots || []).map(s => {
    const match = dcaNodes.find(n => n.order === (s as SlotContent)?.position);
    if (match) {
      return { ...s, payload: match.payload, type: 'dca' };
    }
    return s;
  });
  return overlaid;
}

// Normalizes raw AEM GraphQL items into the CF structure.
export function normalizeCfItems(nodes: CfNode[] | ExtendedCfNode[], tabName: string, userState: boolean): CfNode[] {
  if (!Array.isArray(nodes) || nodes.length === 0) return [];

  const count = nodes.length;
  const result = new Array(count);

  for (let i = 0; i < count; i++) {
    const item = (nodes[i] || {}) as NormalizableCfNode;
    const icon = item.icon;
    const badgeIcon = item.badgeIcon;
    const brandLogoIcon = item.brandLogoIcon;
    const tags = item.tagsList;
    let tagListItems: { tagLabel: string }[] = [];
    if (tags && tags.length > 0) {
      const tCount = tags.length;
      tagListItems = new Array(tCount);
      for (let t = 0; t < tCount; t++) {
        tagListItems[t] = { tagLabel: tags[t] };
      }
    }
    const baseCard = {
      ...item,
      id: item.id || `${tabName}-${Math.random().toString(36).slice(2)}`,
      tabName,
      icon: icon && icon.length ? icon[0] : '',
      description: (item.description as { plaintext?: string })?.plaintext || '',
      badgeIcon: badgeIcon && badgeIcon.length ? badgeIcon[0] : '',
      brandLogoIcon: brandLogoIcon && brandLogoIcon.length ? brandLogoIcon[0] : '',
      tagListItems: tagListItems || [],
    };

    switch (item.cardType) {
      case 'EnrollmentCardModel':
        result[i] = {
          ...baseCard,
          iconAltText_1: item.iconAltText_1,
          iconAltText_2: item.iconAltText_2,
          iconAltText_3: item.iconAltText_3,
          iconAltText_4: item.iconAltText_4,
          iconPath_1: item.iconPath_1,
          iconPath_2: item.iconPath_2,
          iconPath_3: item.iconPath_3,
          iconPath_4: item.iconPath_4,
          enrollmentCardDescription: userState ? item.descriptionLoggedIn : item.descriptionLoggedOut,
          header: userState ? item.headerLoggedIn : item.headerLoggedOut,
          altText: item.imageAltText,
          primaryCtaLink: userState ? item.ctaLinkLoggedIn : item.primaryCtaLink,
          primaryCtaLinkText: userState ? item.ctaLinkTextLoggedIn : item.primaryCtaLinkText,
          primaryCtaOpenInNewTab: userState ? item.ctaOpenInNewTabLoggedIn : item.primaryCtaOpenInNewTab,
          secondaryCtaLink: item.secondaryCtaLink,
          secondaryCtaLinkText: item.secondaryCtaLinkText,
          secondaryCtaOpenInNewTab: item.secondaryCtaOpenInNewTab,
          theme: item.theme,
        };
        break;
      case 'CoBrandCardModel':
      case 'LayeredFeatureCardModel':
      default:
        result[i] = baseCard;
        break;
    }
  }

  return result;
}

// Local mock builder for dev/test use
export function buildLocalDcaNodes(authoredTiles: DcaNode[] = []) {
  return (Array.isArray(authoredTiles) ? authoredTiles : []).map(dt => {
    const order = Number(dt.order ?? 0);
    const mbox = dt.mboxParameter;
    return {
      order,
      mbox,
      payload: {
        mboxName: `${mbox}-${order}`,
        metadata: {
          campaignId: '7277',
          countryCode: 'US',
          endDate: '2026-02-28',
          issuerCode: 'CH',
          loyaltyBar: false,
          mathOffer: false,
          merTag: 'MER_GLB000759O_GLE000HBFR_GLF0016TJE',
          offerCode: 'Required',
          prequal: false,
          rpcCode: 'TEMP',
          startDate: '2026-02-01',
          theme: 'Light',
          title: '7277_US/Canada Members & Non-Members_DEMAND_GEN_FEBRUARY_2026_MERCHANDISING_USCA',
          type: 'CoBrand',
        },
        impressionValue: 'mock-mbox,type=dca,msg=dca,tag=mock-tag',
        image: {
          __typename: 'DCADacImage',
          alternateDescription: 'Amelia Island Florida couple walks the beach',
          caption: '',
          defaultImage: false,
          imageSrc:
            '/content/dam/marriott-digital/digital-merchandising/us-canada/en_us/hero/assets/pdt-DME-Joe-Maddie-Florida-201997538655644.jpg',
          imageUrls: {
            __typename: 'DCADacImageRendition',
            classicHorizontal: '/is/image/marriotts7prod/pdt-DME-Joe-Maddie-Florida-201997538655644:Classic-Hor',
            classicVertical: '/is/image/marriotts7prod/pdt-DME-Joe-Maddie-Florida-201997538655644:Classic-Ver',
            deviceSmall: null,
            featureHorizontal: '/is/image/marriotts7prod/pdt-DME-Joe-Maddie-Florida-201997538655644:Feature-Hor',
            featureVertical: '/is/image/marriotts7prod/pdt-DME-Joe-Maddie-Florida-201997538655644:Feature-Ver',
            flex: null,
            panoramicHorizontal: '/is/image/marriotts7prod/pdt-DME-Joe-Maddie-Florida-201997538655644:Pano-Hor',
            panoramicVertical: '/is/image/marriotts7prod/pdt-DME-Joe-Maddie-Florida-201997538655644:Pano-Ver',
            square: '/is/image/marriotts7prod/pdt-DME-Joe-Maddie-Florida-201997538655644:Square',
            standardHorizontal: '/is/image/marriotts7prod/pdt-DME-Joe-Maddie-Florida-201997538655644:Standard-Hor',
            standardVertical: '/is/image/marriotts7prod/pdt-DME-Joe-Maddie-Florida-201997538655644:Standard-Ver',
            wideHorizontal: '/is/image/marriotts7prod/pdt-DME-Joe-Maddie-Florida-201997538655644:Wide-Hor',
            wideVertical: '/is/image/marriotts7prod/pdt-DME-Joe-Maddie-Florida-201997538655644:Wide-Ver',
          },
          is360Photo: false,
          isPrimary: false,
          isPrimaryArchitectural: false,
          isPrimaryServices: false,
          isSecondaryArchitectural: false,
          isSecondaryServices: false,
          marketingCaption: '',
          roomPoolCodes: [],
          roomTypeCodes: [],
          title: 'DME Joe Maddie Florida',
        },
        headerLong: 'Earn a $250 eGiftCard + up to 100,000 Points',
        headerMedium: 'Earn a $250 eGiftCard + up to 100,000 Points',
        headerShort: 'Save on our Best Rooms',
        descriptionLong: 'Use the $250 eGiftCard on an upcoming stay. Plus, earn up to 17X total points.',
        descriptionMedium: 'Use the $250 eGiftCard on an upcoming stay. Plus, earn up to 17X total points.',
        descriptionShort: 'Elevate your stay',
        callToActionText1: 'Apply Now',
        destinationUrl1: 'https://www.marriott.com/offers/grand-getaways-off-97645',
        destinationUrl2: 'https://www.marriott.com',
        callToActionText2: 'Book Now',
        __typename: 'DCADynamicContentMbox',
        adData: null,
        analyticsName: null,
        bullet1: null,
        bullet2: null,
        bullet3: null,
        caption: null,
        captionUrl: null,
        eVar48: null,
        eyebrowText: 'SUITE MOMENTS',
        imageAssetId:
          'L2NvbnRlbnQvZGFtL21hcnJpb3R0LWRpZ2l0YWwvZGlnaXRhbC1tZXJjaGFuZGlzaW5nL3VzLWNhbmFkYS9lbl91cy9oZXJvL2Fzc2V0cy9wZHQtRE1FLUpvZS1NYWRkaWUtRmxvcmlkYS0yMDE5OTc1Mzg2NTU2NDQuanBn',
        legalLong:
          '*Optional 150 max cc including white space, lorem ispum dolor sit amet, consectetu adipsiicing elit, set alitis do eiusmod tempor incididut ut labore.',
        legalShort: null,
        localeOfContent: 'en_US',
        logo: null,
        logoAssetId: null,
        mathLabel1: null,
        mathLabel2: null,
        mathLabel3: null,
        mathTitle: null,
        mathType: null,
        mathValue: null,
        metaTags: null,
        promotionalTag: null,
        tertiaryImage: null,
      },
    };
  });
}
